import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Countersong (Act III, ACT3-BIBLE.md §7) — the first reflect in the game.
 *
 * A close arc swept in front of the player. Its own shots are unremarkable;
 * what makes it the Countersong is that the arc **converts enemy bullets it
 * touches into friendly shots**, sent back the way they came. Orbit Ward used
 * to simply delete incoming fire and gave that up; this gives it back instead,
 * which is a different verb entirely — the more the boss fires, the more the
 * weapon does, and the fight's own pressure becomes the ammunition.
 *
 * The conversion is a cross-pool interaction: boss bullets live in
 * SpawningSystem's BulletPool and the player's shots in CombatSystem's
 * ProjectilePool, and the only place that holds both is Game (see
 * `_convertBossBullets`, which reads `arcCenter`/`arcConfig` off this instance).
 * Keeping it there rather than reaching across from a weapon is the same call
 * Orbit Ward's bullet-blocking already made.
 *
 * Fugue's starter, and like every starter usable by anyone.
 */
export class Countersong extends Weapon {
  // `config` is a parameter only so the Antiphon fusion can inherit the whole
  // held-arc/reflect apparatus (which Game._convertBossBullets duck-types
  // against) while running on its own FUSED_WEAPONS numbers.
  constructor(config = WEAPONS.countersong) {
    super(config);
    // Where the arc is right now, refreshed each fire — read by Game for the
    // conversion sweep and by the renderer for the arc itself. Null until the
    // first shot, so a freshly-added Countersong converts nothing.
    this.arcCenter = null;
    this.arcAngle = 0;
    this._reflectCooldown = 0;
  }

  /**
   * The arc tracks the player continuously — it is a held stance, not a shot,
   * so conversion can't only happen on the frames the weapon happens to fire.
   * What bounds it instead is `reflectInterval`: the arc turns one bullet
   * around at a time, on a cadence, so a dense wall pays out steadily rather
   * than being reflected wholesale into a screen-clearing burst.
   */
  update(dt, origin, target, pool) {
    this.arcCenter = { x: origin.x, y: origin.y };
    this.arcAngle = target
      ? Math.atan2(target.y - origin.y, target.x - origin.x)
      : Math.atan2(origin.facing?.y ?? -1, origin.facing?.x ?? 0);
    if (this._reflectCooldown > 0) this._reflectCooldown -= dt;
    super.update(dt, origin, target, pool);
  }

  /** True while the arc can turn another bullet around (see Game._convertBossBullets). */
  get reflectReady() {
    return this._reflectCooldown <= 0;
  }

  /** Called by Game once a bullet has actually been converted. */
  noteReflect() {
    this._reflectCooldown = this.config.reflectInterval;
  }

  /** Converted bullets scale with the weapon, not with what fired them. */
  get reflectDamage() {
    return this.config.reflectDamage + this.level * this.config.reflectDamagePerLevel;
  }

  /** The arc widens with level — more of the screen's fire becomes yours. */
  get arcWidth() {
    return this.config.arcWidth * (1 + this.level * 0.06);
  }

  get arcRadius() {
    return this.config.arcRadius + this.level * 2;
  }

  _shotDamage() {
    return this.config.damage + this.level * 1.8;
  }

  fire(origin, target, pool) {
    const angle = this.arcAngle;
    // Three shots across the sweep — the arc's own voice, quiet by design. The
    // weapon's damage is meant to come from what it turns around, so making the
    // baseline strong would make the reflect decorative.
    for (let i = -1; i <= 1; i++) {
      const shotAngle = angle + i * (this.arcWidth / 3);
      pool.fire({
        x: origin.x,
        y: origin.y,
        vx: Math.cos(shotAngle) * this.config.projectileSpeed,
        vy: Math.sin(shotAngle) * this.config.projectileSpeed,
        radius: this.config.projectileRadius,
        damage: this._shotDamage(),
        hits: 1,
        lifetime: this.config.lifetime,
        color: this.config.color,
      });
    }
  }

  /** True if (x, y) is inside the arc as it currently stands. */
  covers(x, y) {
    if (!this.arcCenter) return false;
    const dx = x - this.arcCenter.x;
    const dy = y - this.arcCenter.y;
    if (Math.hypot(dx, dy) > this.arcRadius) return false;
    let diff = Math.atan2(dy, dx) - this.arcAngle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return Math.abs(diff) <= this.arcWidth / 2;
  }

  /** Fusion/removal hook — the arc must not linger once the weapon is gone. */
  dispose() {
    this.arcCenter = null;
  }
}
